import {
  AddOrganisationTicket,
  ApplicationAccessBlock,
  ApplicationAssignment,
  coerceNumericId,
  coerceTicketType,
  formatUserOrNominee,
  getRequestStatusLabel,
  getTicketTypeLabel,
  ProjectAccessTicket,
  ProjectCreateTicket,
  REQUEST_TICKET_TYPE_ADD_ORGANISATION,
  REQUEST_TICKET_TYPE_PROJECT_ACCESS,
  REQUEST_TICKET_TYPE_PROJECT_CREATE,
  REQUEST_TICKET_TYPE_WORKSPACE_CREATE,
  UserOrNominee,
  WorkspaceCreateTicket,
} from '@/pages/Subscriptions/Requests/types'
import { EMPTY_TICKET_LOOKUPS, formatMappedName, TicketLookupMaps } from './ticketLookups'

export type ParsedTicket =
  | { type: 'projectAccess'; data: ProjectAccessTicket }
  | { type: 'projectCreate'; data: ProjectCreateTicket }
  | { type: 'addOrganisation'; data: AddOrganisationTicket }
  | { type: 'workspaceCreate'; data: WorkspaceCreateTicket }
  | { type: 'invalid'; raw: string }

function toCamelCaseKey(key: string): string {
  return key.charAt(0).toLowerCase() + key.slice(1)
}

function camelCaseKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelCaseKeys)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
        toCamelCaseKey(key),
        camelCaseKeys(nested),
      ])
    )
  }
  return value
}

export function parseTicketPayload(ticketType: unknown, ticketJson: string | undefined): ParsedTicket {
  if (!ticketJson) return { type: 'invalid', raw: '{}' }

  try {
    const parsed = camelCaseKeys(JSON.parse(ticketJson))
    switch (coerceTicketType(ticketType)) {
      case REQUEST_TICKET_TYPE_PROJECT_ACCESS:
        return { type: 'projectAccess', data: parsed as ProjectAccessTicket }
      case REQUEST_TICKET_TYPE_PROJECT_CREATE:
        return { type: 'projectCreate', data: parsed as ProjectCreateTicket }
      case REQUEST_TICKET_TYPE_ADD_ORGANISATION:
        return { type: 'addOrganisation', data: parsed as AddOrganisationTicket }
      case REQUEST_TICKET_TYPE_WORKSPACE_CREATE:
        return { type: 'workspaceCreate', data: parsed as WorkspaceCreateTicket }
      default:
        return { type: 'invalid', raw: ticketJson }
    }
  } catch {
    return { type: 'invalid', raw: ticketJson }
  }
}

function addTicketPersonIds(ids: Set<number>, person: UserOrNominee | undefined): void {
  const userId = coerceNumericId(person?.userId)
  if (userId != null) ids.add(userId)
}

function addTicketPeopleIds(ids: Set<number>, people: UserOrNominee[] | undefined): void {
  for (const person of people ?? []) addTicketPersonIds(ids, person)
}

export function collectTicketUserIds(requests: { ticketType: unknown; ticket?: string | null; createdBy?: number | null }[]): number[] {
  const ids = new Set<number>()

  for (const request of requests) {
    const parsed = parseTicketPayload(request.ticketType, request.ticket ?? undefined)
    if (parsed.type === 'invalid') continue

    switch (parsed.type) {
      case 'projectAccess':
        for (const block of parsed.data.applications ?? []) {
          for (const assignment of block.assignments) addTicketPersonIds(ids, assignment)
        }
        break
      case 'projectCreate':
      case 'addOrganisation':
        addTicketPeopleIds(ids, parsed.data.projectLeaders)
        addTicketPeopleIds(ids, parsed.data.projectAdministrators)
        addTicketPeopleIds(ids, parsed.data.projectMembers)
        for (const block of parsed.data.applications ?? []) {
          for (const assignment of block.assignments) addTicketPersonIds(ids, assignment)
        }
        break
      case 'workspaceCreate':
        for (const assignment of parsed.data.people ?? []) addTicketPersonIds(ids, assignment)
        break
    }

    if (request.createdBy != null) ids.add(request.createdBy)
  }

  return [...ids]
}

function formatPeople(
  label: string,
  people: UserOrNominee[] | undefined,
  lookups: TicketLookupMaps
): string[] {
  if (!people?.length) return []
  return [
    `${label}: ${people
      .map((person) => {
        const userId = coerceNumericId(person.userId)
        return formatUserOrNominee(person, userId != null ? lookups.userNames.get(userId) : undefined)
      })
      .join(', ')}`,
  ]
}

function formatRoleNames(
  applicationId: number,
  roleIds: number[] | undefined,
  lookups: TicketLookupMaps
): string {
  if (!roleIds?.length) return 'no roles'

  const appRoleNames = lookups.roleNamesByApplication.get(applicationId)
  return roleIds
    .map((roleId) => {
      const normalizedRoleId = coerceNumericId(roleId) ?? roleId
      const appRoleName = appRoleNames?.get(normalizedRoleId)
      if (appRoleName) return appRoleName
      return formatMappedName(normalizedRoleId, lookups.roleNames, 'Role')
    })
    .join(', ')
}

function formatAssignments(
  applicationId: number,
  assignments: ApplicationAssignment[] | undefined,
  lookups: TicketLookupMaps
): string[] {
  return (assignments ?? []).map((assignment) => {
    const userId = coerceNumericId(assignment.userId)
    const person = formatUserOrNominee(assignment, userId != null ? lookups.userNames.get(userId) : undefined)
    const roles = formatRoleNames(applicationId, assignment.roleIds, lookups)
    return `  • ${person} — ${roles}`
  })
}

function formatApplications(
  applications: ApplicationAccessBlock[] | undefined,
  lookups: TicketLookupMaps
): string[] {
  if (!applications?.length) return []

  return applications.flatMap((block) => [
    `${formatMappedName(block.applicationId, lookups.applicationNames, 'Application')}:`,
    ...formatAssignments(block.applicationId, block.assignments, lookups),
  ])
}

export function buildTicketSummaryLines(
  ticketType: unknown,
  ticketJson: string | undefined,
  subject?: string | null,
  lookups: TicketLookupMaps = EMPTY_TICKET_LOOKUPS
): string[] {
  const parsed = parseTicketPayload(ticketType, ticketJson)
  const typeLabel = getTicketTypeLabel(ticketType)

  const lines = [`Type: ${typeLabel}`]

  switch (parsed.type) {
    case 'projectAccess':
      lines.push(`Project: ${getTicketSubject(ticketType, ticketJson, subject, lookups.projectNames)}`)
      lines.push(...formatApplications(parsed.data.applications, lookups))
      break
    case 'projectCreate':
      lines.push(`Project name: ${parsed.data.projectName}`)
      lines.push(`Frequency: ${parsed.data.frequency}`)
      lines.push(...formatPeople('Leaders', parsed.data.projectLeaders, lookups))
      lines.push(...formatPeople('Administrators', parsed.data.projectAdministrators, lookups))
      lines.push(...formatPeople('Members', parsed.data.projectMembers, lookups))
      lines.push(...formatApplications(parsed.data.applications, lookups))
      break
    case 'workspaceCreate':
      lines.push(`Project: ${formatMappedName(parsed.data.projectId, lookups.projectNames, 'Project')}`)
      lines.push(`Application: ${formatMappedName(parsed.data.applicationId, lookups.applicationNames, 'Application')}`)
      if (parsed.data.people?.length) {
        lines.push('People:')
        lines.push(...formatAssignments(parsed.data.applicationId, parsed.data.people, lookups))
      }
      break
    case 'addOrganisation':
      lines.push(`Organisation: ${parsed.data.organisation.name} (${parsed.data.organisation.longName})`)
      lines.push(`Domain: ${parsed.data.organisation.domain}`)
      lines.push(`Description: ${parsed.data.organisation.description}`)
      lines.push(`Initial project: ${parsed.data.projectName}`)
      lines.push(`Frequency: ${parsed.data.frequency}`)
      lines.push(...formatPeople('Leaders', parsed.data.projectLeaders, lookups))
      lines.push(...formatPeople('Administrators', parsed.data.projectAdministrators, lookups))
      lines.push(...formatPeople('Members', parsed.data.projectMembers, lookups))
      lines.push(...formatApplications(parsed.data.applications, lookups))
      break
    case 'invalid':
      lines.push('Could not parse ticket payload.')
      break
  }

  return lines
}

export function formatCreatedBy(userId: number | null | undefined, lookups: TicketLookupMaps): string {
  return formatMappedName(userId, lookups.userNames, 'User')
}

export function statusLabel(status: unknown): string {
  return getRequestStatusLabel(status)
}

export function getTicketSubject(
  ticketType: unknown,
  ticketJson: string | undefined,
  fallback?: string | null,
  projectNames?: Map<number, string>,
  applicationNames?: Map<number, string>
): string {
  if (fallback?.trim()) return fallback

  const parsed = parseTicketPayload(ticketType, ticketJson)
  switch (parsed.type) {
    case 'projectAccess': {
      const projectId = parsed.data.projectId
      if (projectId && projectNames?.has(projectId)) return projectNames.get(projectId)!
      return projectId ? `Project #${projectId}` : '—'
    }
    case 'projectCreate':
      return parsed.data.projectName?.trim() || '—'
    case 'addOrganisation':
      return parsed.data.organisation?.name?.trim() || '—'
    case 'workspaceCreate':
      return formatMappedName(parsed.data.applicationId, applicationNames ?? new Map(), 'Application')
    default:
      return '—'
  }
}
